// Auth service — session bootstrap (GET /me), profile update, account setup/patch/rename/delete/leave, logout.
//
// Pairing: bootstrap → `useAccountQuery` via `fetchMeAccount` / `getMeAccountCache`;
// profile update → `useUpdateAccountMutation`; setup/patch/rename/delete → account profile mutations.

import { httpService } from 'src/services/app/http.service'
import { log } from 'src/services/app/log.service'
import { localStorageService } from 'src/services/app/storage.service'
import {
  ACCOUNT_ENDPOINTS,
  AUTH_ENDPOINTS,
  INVITE_ENDPOINTS,
  STORAGE_KEYS,
} from 'src/services/consts/api.const'
import {
  createInvalidInviteVerification,
  createTrialExpiredHttpError,
  isTrialExpiredByDate,
  normalizeAccountData,
  normalizeAccountProfileResponse,
  normalizeDeleteAccountResponse,
  normalizeInviteVerification,
  normalizeLeaveAccountResponse,
  resolveInviteVerificationFailureReason,
} from 'src/services/helpers/auth.helper'
import { isSameEmail } from 'src/services/helpers/util.helper'

export const ME_CACHE_TTL_MS = 8 * 60 * 60 * 1000

export const authService = {
  getMeAccountCache,
  fetchMeAccount,
  updateAccount,
  setupAccount,
  patchAccount,
  renameAccount,
  deleteAccount,
  leaveAccount,
  logout,
  verifyInvite,
}

/**
 * Read normalized GET /me data from localStorage when the 8h cache is still valid.
 *
 * @param {{ sessionEmail?: string|null }} [options]
 * @returns {import('src/services/helpers/auth.helper').AccountData|undefined}
 */
function getMeAccountCache({ sessionEmail } = {}) {
  const cached = localStorageService.getCachedData(STORAGE_KEYS.ME_ACCOUNT_CACHE)
  if (cached && sessionEmail && isSameEmail(cached.email, sessionEmail)) {
    if (!isTrialExpiredByDate(cached)) return cached
    localStorageService.remove(STORAGE_KEYS.ME_ACCOUNT_CACHE)
  }
  if (cached && sessionEmail) {
    localStorageService.remove(STORAGE_KEYS.ME_ACCOUNT_CACHE)
  }
  return undefined
}

/**
 * Always fetch GET /me from the network and refresh ME_ACCOUNT_CACHE.
 *
 * @returns {Promise<import('src/services/helpers/auth.helper').AccountData>}
 */
async function fetchMeAccount() {
  try {
    const data = await httpService.get(AUTH_ENDPOINTS.ME)
    if (!data || typeof data !== 'object' || !data.userId) {
      throw new Error('fetchMeAccount: invalid response from server')
    }
    const normalized = normalizeAccountData(data)
    if (isTrialExpiredByDate(normalized)) {
      throw createTrialExpiredHttpError()
    }
    localStorageService.cacheData(STORAGE_KEYS.ME_ACCOUNT_CACHE, normalized, ME_CACHE_TTL_MS)
    log.info('[Session] Account data loaded')
    return normalized
  } catch (error) {
    const status = error?.response?.status

    if (status === 401 || status === 404) {
      localStorageService.remove(STORAGE_KEYS.ME_ACCOUNT_CACHE)
    }
    throw error
  }
}

/**
 * Partial-update the authenticated user's profile via PATCH /me.
 *
 * @param {UpdateAccountPayload} payload
 * @returns {Promise<import('src/services/helpers/auth.helper').AccountData>}
 */
async function updateAccount(payload) {
  const data = await httpService.patch(AUTH_ENDPOINTS.ME, payload)
  if (!data || typeof data !== 'object' || !data.userId) {
    throw new Error('updateAccount: invalid response from server')
  }
  const normalized = normalizeAccountData(data)
  localStorageService.cacheData(STORAGE_KEYS.ME_ACCOUNT_CACHE, normalized, ME_CACHE_TTL_MS)
  return normalized
}

/**
 * Rename the current account via POST /account/rename.
 *
 * @param {{ accountName: string }} payload
 * @returns {Promise<import('src/services/helpers/auth.helper').AccountProfileResponse>}
 */
async function renameAccount({ accountName }) {
  const data = await httpService.post(ACCOUNT_ENDPOINTS.RENAME, { accountName })
  if (!data || typeof data !== 'object' || !data.accountId || data.accountName == null) {
    throw new Error('renameAccount: invalid response from server')
  }
  return normalizeAccountProfileResponse(data)
}

/**
 * Delete the current account via POST /account/delete.
 *
 * @returns {Promise<import('src/services/helpers/auth.helper').DeleteAccountResponse>}
 */
async function deleteAccount() {
  const data = await httpService.post(ACCOUNT_ENDPOINTS.DELETE)
  if (!data || typeof data !== 'object' || !data.accountId || data.deletedAt == null) {
    throw new Error('deleteAccount: invalid response from server')
  }
  return normalizeDeleteAccountResponse(data)
}

/**
 * Leave the current account via POST /me/leave-account.
 *
 * @returns {Promise<import('src/services/helpers/auth.helper').LeaveAccountResponse>}
 */
async function leaveAccount() {
  const data = await httpService.post(AUTH_ENDPOINTS.LEAVE_ACCOUNT)
  const normalized = normalizeLeaveAccountResponse(data)
  if (!normalized) {
    throw new Error('leaveAccount: invalid response from server')
  }
  return normalized
}

/**
 * Initial account setup via POST /account/setup.
 *
 * @param {{ accountName: string, companyName?: string|null, country?: string|null }} payload
 * @returns {Promise<import('src/services/helpers/auth.helper').AccountData>}
 */
async function setupAccount({ accountName, companyName, country }) {
  const payload = {
    accountName,
    companyName: companyName?.trim() || null,
    country: country || null,
  }
  const data = await httpService.post(ACCOUNT_ENDPOINTS.SETUP, payload)
  if (!data || typeof data !== 'object' || !data.userId) {
    throw new Error('setupAccount: invalid response from server')
  }
  return normalizeAccountData(data)
}

/**
 * Account profile update via PATCH /account.
 *
 * @param {{ accountName: string, companyName?: string|null, country?: string|null }} payload
 * @returns {Promise<import('src/services/helpers/auth.helper').AccountData>}
 */
async function patchAccount({ accountName, companyName, country }) {
  const payload = {
    accountName,
    companyName: companyName?.trim() || null,
    country: country || null,
  }
  const data = await httpService.patch(ACCOUNT_ENDPOINTS.PATCH, payload)
  if (!data || typeof data !== 'object' || !data.userId) {
    throw new Error('patchAccount: invalid response from server')
  }
  return normalizeAccountData(data)
}

/**
 * Backend logout — best-effort so Auth0 logout proceeds even on failure.
 *
 * @returns {Promise<void>}
 */
async function logout() {
  try {
    await httpService.post(AUTH_ENDPOINTS.LOGOUT)
    log.info('[Session] Backend logout successful')
  } catch (error) {
    log.error('[Session] Backend logout failed', error)
  }
}

/**
 * Verify an invite token for the landing page (public endpoint).
 *
 * Fails safe: every error resolves to a not-valid verification instead of
 * throwing, so the public landing can never blank out. The `reason` carries WHY
 * it failed — `resolveInviteVerificationFailureReason` separates a token the
 * backend rejected from one it never got to judge, so the page only blames the
 * link when the backend actually did.
 *
 * @param {string} token
 * @returns {Promise<import('src/services/helpers/auth.helper').InviteVerification>}
 */
async function verifyInvite(token) {
  try {
    const data = await httpService.get(INVITE_ENDPOINTS.VERIFY(token))
    return normalizeInviteVerification(data)
  } catch (error) {
    log.error('[Invite] verify failed', error)
    return createInvalidInviteVerification(resolveInviteVerificationFailureReason(error))
  }
}
